<template>
  <div class="card my-3">
    <div class="card-header d-flex align-items-center">
      <div class="me-auto text-uppercase">
        <i class="fas fa-bars grabbable"></i>
        {{ blockTypes[block.type] | translate }}
      </div>
      <div v-if="_includes(['button', 'video'], block.type)">
        <div class="btn-group" role="group">
          <button
            :id="`btnGroupDrop${block.id}`"
            type="button"
            class="btn btn-outline-primary dropdown-toggle"
             data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          ></button>
          <div
            class="dropdown-menu"
            :aria-labelledby="`btnGroupDrop${block.id}`"
          >
            <a href="#" class="dropdown-item" @click.prevent="duplicate">
              {{ 'Duplicate' | translate }}
            </a>
            <a href="#" class="dropdown-item" @click.prevent="copyTo">
              {{ 'Copy to' | translate }}
            </a>
            <a href="#" class="dropdown-item" @click.prevent="remove">
              {{ 'Delete' | translate }}
            </a>
          </div>
        </div>
      </div>
      <div v-else>
        <div
          v-if="editing"
          class="btn-group"
          role="group"
          aria-label="Button group with nested dropdown"
        >
          <button @click.prevent="save" class="btn btn-primary">
            {{ 'Save' | translate }}
          </button>
          <button @click.prevent="cancel" class="btn btn-outline-primary">
            {{ 'Cancel' | translate }}
          </button>
        </div>
        <div
          v-else
          class="btn-group"
          role="group"
          aria-label="Button group with nested dropdown"
        >
          <button class="btn btn-outline-primary" @click.prevent="edit">
            <i class="fas fa-pencil-alt"></i>
          </button>
          <div class="btn-group" role="group">
            <button
              :id="`btnGroupDrop${block.id}`"
              type="button"
              class="btn btn-outline-primary dropdown-toggle"
               data-bs-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            ></button>
            <div
              class="dropdown-menu"
              :aria-labelledby="`btnGroupDrop${block.id}`"
            >
              <a href="#" class="dropdown-item" @click.prevent="duplicate">
                {{ 'Duplicate' | translate }}
              </a>
              <a href="#" class="dropdown-item" @click.prevent="copyTo">
                {{ 'Copy to' | translate }}
              </a>
              <div class="dropdown-divider"></div>
              <a href="#" class="dropdown-item" @click.prevent="remove">
                {{ 'Delete' | translate }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body" v-if="!_includes(['button'], block.type)">
      <TextBlock
        v-if="block.type === 'text'"
        :block="block"
        :editing="editing"
        @quillChanged="quillChanged"
      ></TextBlock>

      <BannerBlock
        v-if="block.type === 'banner'"
        :block="block"
        :editing="editing"
        @fileDeleted="deleteFile"
        @fileUploaded="saveFile"
      ></BannerBlock>

      <BioBlock
        v-if="block.type === 'bio'"
        :block="block"
        :editing="editing"
        @fileDeleted="deleteFile"
        @fileUploaded="saveFile"
      ></BioBlock>

      <QuoteBlock
        v-if="block.type === 'quote'"
        :block="block"
        :editing="editing"
      ></QuoteBlock>

      <ChecklistBlock
        v-if="block.type === 'checklist'"
        :block="block"
        :editing="editing"
      ></ChecklistBlock>

      <FaqBlock
        v-if="block.type === 'faq'"
        :block="block"
        :editing="editing"
      ></FaqBlock>

      <ColumnsBlock
        v-if="block.type === 'columns'"
        :block="block"
        :editing="editing"
        @fileDeleted="saveAndContinue"
        @fileUploaded="saveAndContinue"
      ></ColumnsBlock>

      <GalleryBlock
        v-if="block.type === 'gallery'"
        :block="block"
        :editing="editing"
        @fileDeleted="saveAndContinue"
        @fileUploaded="saveAndContinue"
      ></GalleryBlock>

      <HtmlBlock
        v-if="block.type === 'html'"
        :block="block"
        :editing="editing"
      ></HtmlBlock>

      <MarkdownBlock
        v-if="block.type === 'markdown'"
        :block="block"
        :editing="editing"
      ></MarkdownBlock>

      <CourseListBlock
        v-if="block.type === 'course-list'"
        :block="block"
        :editing="editing"
      ></CourseListBlock>

      <ImageBlock
        v-if="block.type === 'image'"
        :block="block"
        :editing="editing"
        @fileDeleted="deleteFile"
        @fileUploaded="saveFile"
      ></ImageBlock>

      <LectureListBlock
        v-if="block.type === 'lecture-list'"
        :block="block"
        :editing="editing"
      ></LectureListBlock>

      <ButtonBlock
        v-if="block.type === 'button'"
        :block="block"
      ></ButtonBlock>

      <SideImageBlock
        v-if="block.type === 'side-image'"
        :block="block"
        :editing="editing"
        @fileDeleted="deleteFile"
        @fileUploaded="saveFile"
      ></SideImageBlock>

      <LeadGenerationBlock
        v-if="block.type === 'lead-generation'"
        :block="block"
        :editing="editing"
      ></LeadGenerationBlock>

      <CountdownTimeBlock
        v-if="block.type === 'countdown-time'"
        :block="block"
        :editing="editing"
      ></CountdownTimeBlock>

      <VideoEmbedBlock
        v-if="block.type === 'video-embed'"
        :block="block"
        :editing="editing"
      ></VideoEmbedBlock>

      <VideoBlock
        v-if="block.type === 'video'"
        :block="block"
        :editing="editing"
        @fileUploaded="save"
      ></VideoBlock>
    </div>
  </div>
</template>

<script>
import route from './../../routes.js'

import TextBlock from './TextBlock'
import ImageBlock from './ImageBlock'
import BannerBlock from './BannerBlock'
import BioBlock from './BioBlock'
import MarkdownBlock from './MarkdownBlock'
import HtmlBlock from './HtmlBlock'
import CourseListBlock from './CourseListBlock'
import LectureListBlock from './LectureListBlock'
import ButtonBlock from './ButtonBlock'
import QuoteBlock from './QuoteBlock'
import ColumnsBlock from './ColumnsBlock'
import SideImageBlock from './SideImageBlock'
import FaqBlock from './FaqBlock'
import VideoEmbedBlock from './VideoEmbedBlock'
import VideoBlock from './VideoBlock'
import LeadGenerationBlock from './LeadGenerationBlock'
import CountdownTimeBlock from './CountdownTimeBlock'
import ChecklistBlock from './ChecklistBlock'
import GalleryBlock from './GalleryBlock'
import FormBlock from './FormBlock'

import _ from 'lodash'
import _includes from 'lodash/includes'

export default {
  components: {
    SideImageBlock,
    TextBlock,
    ImageBlock,
    BannerBlock,
    BioBlock,
    QuoteBlock,
    HtmlBlock,
    MarkdownBlock,
    CourseListBlock,
    LectureListBlock,
    ButtonBlock,
    FaqBlock,
    ColumnsBlock,
    LeadGenerationBlock,
    CountdownTimeBlock,
    ChecklistBlock,
    VideoEmbedBlock,
    VideoBlock,
    GalleryBlock,
    FormBlock,
  },
  props: ['blockData'],
  data() {
    return {
      editing: false,
    }
  },
  methods: {
    duplicate() {
      this.$emit('blockDuplicated', this.blockData)
      // axios.post(`/home/subpages/${this.blockData.subpage_id}/blocks/${this.blockData.id}/copy-to`)
      // .then(response => {
        
      // })
    },
    copyTo() {
      this.fetchCourses()
        .then((courses) => {
          return Swal.fire({
            title: this.$t('Select a course'),
            input: 'select',
            inputOptions: [this.$t('Main page'), ...courses.map((course) => course.title)],
            showCancelButton: true,
            cancelButtonText: this.$t('Cancel'),
          })
        })
        .then((selected) => {
          if (selected.value == 0) {
            return {id: 'main-page'}
          }
          else if (selected.value) {
            return this.courses[selected.value - 1]
          }
          throw new Error("No course selected");
        })
        .then(course => {
          return axios.post(`/home/subpages/${this.blockData.subpage_id}/blocks/${this.blockData.id}/copy-to`, course)
        })
        .then(response => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    fetchCourses() {
      return new Promise((resolve, reject) => {
        if (this.courses.length) {
          resolve(this.courses)
        }
        axios
          .post('/json/courses')
          .then((response) => {
            this.$store.commit('coursesUpdate', response.data)
            resolve(response.data)
          })
          .catch((e) => {
            reject(e)
          })
      })
    },
    _includes,
    edit() {
      this.editing = true
    },
    handleError(error) {
      if (error.message === 'No course selected') {
        return null
      }
      if (error.response && error.response.status === 419) {
        this.$root.$emit(
          'flash',
          this.$t('Your session has expired - please reload'),
          'warning'
        )
      } else {
        iziToast.error({
          title: 'Error',
          message: this.$t('Something went wrong'),
          position: 'bottomLeft',
        })
        console.error(error)
      }
    },
    deleteFile() {
      axios
        .put(route.block({ id: this.block.subpage_id }, this.block).update, {
          file: null,
        })
        .then((response) => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Deleted'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    saveFile() {
      axios
        .put(route.block({ id: this.block.subpage_id }, this.block).update, {
          file: this.block.file,
        })
        .then((response) => {
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    saveAndContinue() {
      axios
        .put(
          route.block({ id: this.block.subpage_id }, this.block).update,
          this.block
        )
        .then((response) => {
          this.block = response.data
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    save() {
      this.editing = false

      axios
        .put(
          route.block({ id: this.block.subpage_id }, this.block).update,
          this.block
        )
        .then((response) => {
          this.block = response.data
          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        })
        .catch(this.handleError)
    },
    cancel() {
      this.editing = false
    },
    remove() {
      Swal.fire({
        title: this.$t('Delete'),
        text: this.$t('Are you sure?'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('OK'),
        cancelButtonText: this.$t('Cancel'),
      })
        .then((result) => {
          if (result.value) {
            return axios.delete(
              route.block({ id: this.block.subpage_id }, this.block).delete
            )
          }
          return false
        })
        .then((response) => {
          if (response) {
            this.$emit('remove', this.block)
          }
        })
        .catch(this.handleError)
    },
    quillChanged(html, quill) {
      this.block = {
        ...this.block,
        html,
        quill,
      }
    },
  },
  computed: {
    courses() {
      return this.$store.state.products
    },
    block: {
      get() {
        return this.blockData
      },
      set(data) {
        this.$emit('blockUpdate', data)
      },
    },
    blockTypes() {
      return this.$store.state.blockTypes
    },
  },
}
</script>
